// One finger on one element: a tap, or a swipe in a direction. The element
// follows the finger while it is down (the transform is written straight on the
// element, with no transition and no React state, so nothing lags a frame) and
// eases back on release. Pair it with `touch-action: none` on the element so the
// browser leaves the gesture alone and never starts a pull-to-refresh.
//
// Anything inside the element marked `data-nodrag` (a close button) keeps its
// own click: the gesture does not start on it.

import { useEffect, useRef, type RefObject } from "react";

export type SwipeDirection = "left" | "right" | "up" | "down";

export interface SwipeOptions {
  readonly onTap?: () => void;
  readonly onSwipe?: (direction: SwipeDirection) => void;
  /** Directions the element may follow the finger in. */
  readonly follow: readonly SwipeDirection[];
}

const MOVE_SLOP = 8;
const SWIPE_DISTANCE = 44;
const FLICK_DISTANCE = 22;
const FLICK_SPEED = 0.45;
const TAP_MS = 700;

export function useSwipe(ref: RefObject<HTMLElement | null>, options: SwipeOptions): void {
  const latest = useRef(options);
  latest.current = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let id: number | null = null;
    let x0 = 0;
    let y0 = 0;
    let t0 = 0;
    let axis: "x" | "y" | null = null;
    let dx = 0;
    let dy = 0;

    const allowed = (d: SwipeDirection): boolean => latest.current.follow.includes(d);

    const reset = (animate: boolean): void => {
      el.style.transition = animate ? "transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1)" : "none";
      el.style.transform = "";
    };

    const down = (e: PointerEvent): void => {
      if (id !== null || (e.pointerType === "mouse" && e.button !== 0)) return;
      if (e.target instanceof Element && e.target.closest("[data-nodrag]")) return;
      id = e.pointerId;
      x0 = e.clientX;
      y0 = e.clientY;
      t0 = e.timeStamp;
      axis = null;
      dx = 0;
      dy = 0;
      el.style.transition = "none";
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        // A synthetic pointer cannot always be captured; the gesture still reads.
      }
    };

    const move = (e: PointerEvent): void => {
      if (e.pointerId !== id) return;
      dx = e.clientX - x0;
      dy = e.clientY - y0;
      if (axis === null && Math.max(Math.abs(dx), Math.abs(dy)) > MOVE_SLOP) {
        axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      }
      if (axis === "x") {
        const shown = dx < 0 ? (allowed("left") ? dx : 0) : allowed("right") ? dx : 0;
        el.style.transform = `translate3d(${shown * 0.9}px, 0, 0)`;
      } else if (axis === "y") {
        const shown = dy < 0 ? (allowed("up") ? dy : 0) : allowed("down") ? dy : 0;
        el.style.transform = `translate3d(0, ${shown * 0.9}px, 0)`;
      }
    };

    const finish = (e: PointerEvent, cancelled: boolean): void => {
      if (e.pointerId !== id) return;
      id = null;
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        // Already released.
      }
      const elapsed = Math.max(1, e.timeStamp - t0);
      const along = axis === "x" ? dx : dy;
      const speed = Math.abs(along) / elapsed;
      const far = Math.abs(along) >= SWIPE_DISTANCE || (Math.abs(along) >= FLICK_DISTANCE && speed >= FLICK_SPEED);
      reset(true);
      if (cancelled) return;
      if (axis === null) {
        if (elapsed <= TAP_MS) latest.current.onTap?.();
        return;
      }
      if (!far) return;
      const direction: SwipeDirection = axis === "x" ? (dx < 0 ? "left" : "right") : dy < 0 ? "up" : "down";
      if (latest.current.follow.includes(direction)) {
        // The next view arrives with its own entrance, so the element that
        // carried the finger does not animate back across it.
        reset(false);
        latest.current.onSwipe?.(direction);
      }
    };

    const up = (e: PointerEvent): void => finish(e, false);
    const cancel = (e: PointerEvent): void => finish(e, true);

    el.addEventListener("pointerdown", down);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", cancel);
    return () => {
      el.removeEventListener("pointerdown", down);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", cancel);
      el.style.transform = "";
      el.style.transition = "";
    };
  }, [ref]);
}
